/**
 * Thin wrapper over the Google Places API (New) Autocomplete Data API.
 *
 * Replaces the deprecated `google.maps.places.Autocomplete` widget, which injected its own
 * `.pac-container` dropdown into <body> and could not be styled with our design system.
 * Here we only fetch predictions; rendering is left to the caller.
 *
 * Requires "Places API (New)" to be enabled on the GCP project owning the browser key.
 */

const IMPORT_LIBRARY_TIMEOUT_MS = 20000;
const IMPORT_LIBRARY_POLL_MS = 100;

// Google supports a rolling window of quarterly versions (3.62-3.65 as of TM-7461) and
// silently serves the newest one for anything older, so a stale pin buys no stability.
// Bump this when the pinned version drops out of the supported window.
const MAPS_API_VERSION = '3.65';

let placesLibraryPromise = null;

function isMapsApiRequested() {
  return typeof window.google?.maps?.importLibrary === 'function'
    || Boolean(document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]'));
}

/**
 * Injects the Maps JS API loader unless the page already requested it (several pages ship
 * their own <script> tag, sometimes with a different API key).
 *
 * The API must only ever be loaded once per page: a second loader breaks the first one
 * ("Element with name gmp-internal-loading-text already defined"). Components needing
 * more libraries than the winning loader listed should use `importMapsLibraries`.
 */
export function ensureMapsApiLoaded({ apiKey = null, libraries = ['places'], language = 'en', region = null } = {}) {
  if (isMapsApiRequested()) {
    return;
  }

  const key = apiKey || window.GOOGLE_MAPS_API_KEY;
  if (!key) {
    throw new Error('Cannot load the Google Maps JS API without an API key');
  }

  const regionParam = region ? `&region=${region}` : '';
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://maps.googleapis.com/maps/api/js?v=${MAPS_API_VERSION}`
    + `&loading=async&libraries=${libraries.join(',')}&language=${language}${regionParam}&key=${key}`;
  document.head.appendChild(script);
}

/**
 * Each page loads the Maps JS API with its own <script> tag, so `importLibrary` may not
 * exist yet when a component mounts. Poll for it instead of racing the loader.
 */
function waitForImportLibrary() {
  return new Promise((resolve, reject) => {
    if (typeof window.google?.maps?.importLibrary === 'function') {
      resolve();
      return;
    }

    const deadline = Date.now() + IMPORT_LIBRARY_TIMEOUT_MS;
    const poll = setInterval(() => {
      if (typeof window.google?.maps?.importLibrary === 'function') {
        clearInterval(poll);
        resolve();
        return;
      }

      if (Date.now() > deadline) {
        clearInterval(poll);
        reject(new Error(
          `Google Maps JS API did not expose importLibrary within ${IMPORT_LIBRARY_TIMEOUT_MS}ms`,
        ));
      }
    }, IMPORT_LIBRARY_POLL_MS);
  });
}

/**
 * Resolves with `google.maps` once every requested library is loaded. Libraries are fetched
 * on demand, so this works regardless of which page loader won and what it listed.
 *
 * @param {string[]} libraries e.g. `['core', 'maps', 'marker', 'geocoding']`
 */
export async function importMapsLibraries(libraries) {
  await waitForImportLibrary();
  await Promise.all(libraries.map(name => window.google.maps.importLibrary(name)));

  return window.google.maps;
}

export function loadPlacesLibrary() {
  placesLibraryPromise ??= waitForImportLibrary()
    .then(() => window.google.maps.importLibrary('places'))
    .catch((cause) => {
      placesLibraryPromise = null;
      throw cause;
    });

  return placesLibraryPromise;
}

function toFieldSuggestion(suggestion) {
  const prediction = suggestion.placePrediction;
  const text = prediction.text.toString();

  return {
    value: text,
    label: text,
    placeId: prediction.placeId,
    types: prediction.types,
    prediction,
  };
}

/**
 * @param {object} options
 * @param {string[]} [options.includedPrimaryTypes] Place types to restrict predictions to (max 5).
 * @param {string[]} [options.includedRegionCodes] CLDR region codes to restrict predictions to.
 * @param {string} [options.language]
 * @param {string} [options.region]
 */
export function createPlacesAutocomplete({
  includedPrimaryTypes = null,
  includedRegionCodes = null,
  language = 'en',
  region = null,
} = {}) {
  const options = { includedPrimaryTypes, includedRegionCodes, language, region };

  let places = null;
  let sessionToken = null;
  let latestRequestId = 0;

  async function getPlacesLibrary() {
    places ??= await loadPlacesLibrary();
    return places;
  }

  function currentSessionToken(placesLibrary) {
    sessionToken ??= new placesLibrary.AutocompleteSessionToken();
    return sessionToken;
  }

  function buildRequest(input, placesLibrary) {
    const request = {
      input,
      language: options.language,
      sessionToken: currentSessionToken(placesLibrary),
    };

    if (options.includedPrimaryTypes?.length) {
      request.includedPrimaryTypes = options.includedPrimaryTypes;
    }
    if (options.includedRegionCodes?.length) {
      request.includedRegionCodes = options.includedRegionCodes;
    }
    if (options.region) {
      request.region = options.region;
    }

    return request;
  }

  /**
   * @returns {Promise<?object[]>} Suggestions, or `null` when a newer keystroke superseded
   *                              this request and the result must be discarded.
   */
  async function fetchSuggestions(input) {
    const query = String(input ?? '').trim();
    if (!query) {
      return [];
    }

    const placesLibrary = await getPlacesLibrary();
    latestRequestId += 1;
    const requestId = latestRequestId;

    const { suggestions } = await placesLibrary.AutocompleteSuggestion
      .fetchAutocompleteSuggestions(buildRequest(query, placesLibrary));

    if (requestId !== latestRequestId) {
      return null;
    }

    return suggestions
      .filter(suggestion => suggestion.placePrediction)
      .map(toFieldSuggestion);
  }

  /**
   * @param {object} suggestion A suggestion previously returned by `fetchSuggestions`.
   * @param {string[]} fields Place fields to fetch, e.g. `['viewport', 'addressComponents']`.
   */
  async function resolvePlace(suggestion, fields) {
    if (!suggestion?.prediction) {
      throw new Error('Cannot resolve a Google place from a suggestion without a place prediction');
    }

    const place = suggestion.prediction.toPlace();
    await place.fetchFields({ fields });

    // Google bills one Autocomplete session per token and the session ends with this
    // details request, so the next keystroke has to open a fresh session.
    sessionToken = null;

    return place;
  }

  function setRegionCodes(regionCodes) {
    options.includedRegionCodes = regionCodes;
  }

  /** Drops the billing session and invalidates in-flight requests. */
  function reset() {
    sessionToken = null;
    latestRequestId += 1;
  }

  return { fetchSuggestions, resolvePlace, setRegionCodes, reset };
}
